import type { DataCard, GeneratedLegCard, GeneratedParlayCard } from './chatTypes';
import { type LeagueKey, leagueKeyOrNull } from './league';
import { LEG_PROVENANCE, type LegProvenanceFields } from './legProvenance';
import { formatStatLabel } from './statLabels';

/** The chat message for tracking a card's parlay when chatCardTrackRequest returns null; chat rebuilds the legs from it. */
export function buildTrackMessage(legs: GeneratedLegCard[]): string {
  const legDescriptions = legs.map((leg) => {
    const player = leg.player || '';
    const overUnder = leg.overUnder || 'Over';
    const line = leg.line != null ? String(leg.line) : '';
    const stat = leg.statType ? formatStatLabel(leg.statType) : leg.prop || '';
    return `${player} ${overUnder} ${line} ${stat}`.trim();
  });
  return `Track this parlay: ${legDescriptions.join(', ')}`;
}

/**
 * Append a data card to a message's cards. A finished generation's card replaces the
 * per-parlay cards streamed before it, which carry no prediction ids.
 */
export function mergeDataCard(cards: DataCard[], card: DataCard): DataCard[] {
  if (card.type === 'parlay_summary' && card.data.final) {
    const withoutStreamed = cards.filter(
      (c) => !(c.type === 'parlay_summary' && c.data.parlayIndex != null),
    );
    return [...withoutStreamed, card];
  }
  return [...cards, card];
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/** One leg of the track request for a parlay shown on a chat generation card. */
export interface ChatTrackLeg extends LegProvenanceFields {
  player: string;
  team?: string | null;
  opponentTeam?: string | null;
  prop: string;
  statType?: string;
  line?: number | null;
  overUnder?: string | null;
  odds?: string | null;
  gameId?: string | null;
  gameDate?: string;
  gameStartsAt?: string;
  marketKey?: string | null;
  leagueKey: LeagueKey;
}

export interface ChatTrackRequest {
  legs: ChatTrackLeg[];
  league: LeagueKey;
  tier?: string;
  totalOdds?: string;
  confidence?: string;
  reasoning?: string;
  sportsbook: string;
  generationId?: string;
}

/**
 * The track request for a generated parlay on a chat card: each leg a generation pick with
 * its prediction row id, so the server links it rather than a language model retyping it.
 * Null when the card cannot be tracked that way: a card saved before picks carried ids, legs
 * from more than one league, or no sportsbook.
 */
export function chatCardTrackRequest(
  parlay: GeneratedParlayCard,
  generationId?: string,
): ChatTrackRequest | null {
  const parlayLeague = leagueKeyOrNull(parlay.leagueKey);
  const legs: ChatTrackLeg[] = [];
  for (const leg of parlay.legs) {
    const leagueKey = leagueKeyOrNull(leg.leagueKey) ?? parlayLeague;
    const predictionId = text(leg.predictionId);
    if (!leagueKey || !predictionId) return null;
    legs.push({
      player: leg.player || leg.team || '',
      team: leg.team ?? null,
      opponentTeam: leg.opponentTeam ?? null,
      prop: leg.prop || '',
      statType: leg.statType ?? undefined,
      line: leg.line ?? null,
      overUnder: leg.overUnder ?? null,
      odds: leg.odds ?? null,
      gameId: text(leg.gameId) ?? null,
      gameDate: text(leg.gameDate),
      gameStartsAt: text(leg.gameStartsAt),
      marketKey: leg.marketKey ?? null,
      leagueKey,
      provenance: LEG_PROVENANCE.generation,
      predictionId,
    });
  }
  const league = legs[0]?.leagueKey;
  if (!league || legs.some((leg) => leg.leagueKey !== league)) return null;
  const sportsbook = text(parlay.sportsbook);
  if (!sportsbook) return null;
  return {
    legs,
    league,
    tier: parlay.name ?? undefined,
    totalOdds: parlay.totalOdds ?? parlay.odds ?? undefined,
    confidence: parlay.confidence ?? undefined,
    reasoning: parlay.reasoning ?? undefined,
    sportsbook,
    generationId,
  };
}
